import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import {
  Loader2, Zap, TrendingUp, Star, Eye, MousePointer, Play, Users,
  Clock, CheckCircle, XCircle, ArrowLeft, BarChart3, RefreshCw
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BUNDLE_ICONS = { starter: Zap, growth: TrendingUp, spotlight: Star };
const STATUS_STYLES = {
  active: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/20' },
  pending: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/20' },
  expired: { bg: 'bg-muted/35', text: 'text-muted-foreground', border: 'border-border/20' },
  cancelled: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/20' },
};

export default function PromoDashboard() {
  const { isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [promos, setPromos] = useState({ active: [], past: [], pending: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchPromos();
    // Check for success redirect
    const sessionId = searchParams.get('session_id');
    if (sessionId) { toast.success('Payment successful! Promotion activating...'); }
  }, [isAuthenticated, authLoading]);

  const fetchPromos = async () => {
    try {
      const res = await fetch(`${API}/promo/active`, { headers: getAuthHeaders() });
      if (res.ok) setPromos(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>;
  }

  const allPromos = [...promos.active, ...promos.pending, ...promos.past];

  return (
    <div className="min-h-screen pb-32" data-testid="promo-dashboard">
      <div className="border-b border-border bg-gradient-to-r from-zinc-950 via-zinc-900/80 to-zinc-950">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-muted-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Promotion Dashboard</h1>
              <p className="text-sm text-muted-foreground">Track your active promotions and performance</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-6 space-y-6">
        {/* Summary Stats */}
        {promos.active.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Zap} label="Active Promos" value={promos.active.length} />
            <StatCard icon={Eye} label="Total Impressions"
              value={promos.active.reduce((s, p) => s + (p.analytics?.impressions || 0), 0)} />
            <StatCard icon={MousePointer} label="Total Clicks"
              value={promos.active.reduce((s, p) => s + (p.analytics?.clicks || 0), 0)} />
            <StatCard icon={Play} label="Total Plays"
              value={promos.active.reduce((s, p) => s + (p.analytics?.plays || 0), 0)} />
          </div>
        )}

        {/* Active */}
        {promos.active.length > 0 && (
          <Section title="Active Promotions" count={promos.active.length}>
            {promos.active.map(p => <PromoCard key={p.purchase_id} promo={p} />)}
          </Section>
        )}

        {/* Pending */}
        {promos.pending.length > 0 && (
          <Section title="Pending Payment" count={promos.pending.length}>
            {promos.pending.map(p => <PromoCard key={p.purchase_id} promo={p} />)}
          </Section>
        )}

        {/* Past */}
        {promos.past.length > 0 && (
          <Section title="Past Promotions" count={promos.past.length}>
            {promos.past.map(p => <PromoCard key={p.purchase_id} promo={p} />)}
          </Section>
        )}

        {allPromos.length === 0 && (
          <Card className="bg-muted/20 border-border p-12 text-center">
            <Zap className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" />
            <p className="text-muted-foreground text-lg font-medium">No promotions yet</p>
            <p className="text-muted-foreground text-sm mt-1">Use the Promote button on any post, track, or video to get started</p>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="bg-muted/20 border-border p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-orange-400" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </Card>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">{title}</h2>
        <Badge className="bg-muted/50 text-foreground/80 text-[10px]">{count}</Badge>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function PromoCard({ promo }) {
  const st = STATUS_STYLES[promo.status] || STATUS_STYLES.pending;
  const BundleIcon = BUNDLE_ICONS[promo.bundle_id] || Zap;
  const analytics = promo.analytics || {};

  return (
    <Card className="bg-muted/20 border-border p-4" data-testid={`promo-${promo.purchase_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center flex-shrink-0">
          <BundleIcon className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm text-white">{promo.bundle_name}</p>
            <Badge className={`${st.bg} ${st.text} ${st.border} text-[10px]`}>{promo.status}</Badge>
            <Badge className="bg-muted/30 text-muted-foreground border-border/50 text-[10px]">{promo.content_type}</Badge>
          </div>

          {promo.status === 'active' && promo.expires_at && (
            <p className="text-xs text-muted-foreground mt-1">
              <Clock className="w-3 h-3 inline mr-1" />
              Expires {formatDistanceToNow(new Date(promo.expires_at), { addSuffix: true })}
            </p>
          )}

          {/* Analytics */}
          {promo.status === 'active' && (
            <div className="flex items-center gap-4 mt-2">
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Eye className="w-3 h-3" />{(analytics.impressions || 0).toLocaleString()} views
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <MousePointer className="w-3 h-3" />{(analytics.clicks || 0).toLocaleString()} clicks
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Play className="w-3 h-3" />{(analytics.plays || 0).toLocaleString()} plays
              </span>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground/80 mt-1">
            {promo.created_at ? formatDistanceToNow(new Date(promo.created_at), { addSuffix: true }) : ''}
          </p>
        </div>

        <div className="text-right flex-shrink-0">
          <p className="font-bold text-sm text-white">${(promo.amount / 100).toFixed(2)}</p>
          <p className="text-[10px] text-muted-foreground">{promo.duration_hours}h</p>
        </div>
      </div>
    </Card>
  );
}
